import { type FormEvent, type ReactNode, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Editor from "@monaco-editor/react";
import Client from "fhir-kit-client";
import i18n from "i18next";

import { addRequiredCategory, type DeviceKind, type FhirDevice } from "../../services/DeviceAdapter";
import VulcanPage from "../../components/VulcanPage/VulcanPage";
import DeviceQuestionnaireRegistration from "./DeviceQuestionnaireRegistration";

import "./DeviceRegistration.css";

type DeviceRegistrationProps = {
  kind: DeviceKind;
};

type RegistrationMode = "form" | "json" | "questionnaire";

type FormListItem = Record<string, string>;

type RepeatableField =
  | "additionalCategories"
  | "additionalIdentifiers"
  | "additionalNotes"
  | "additionalTypes"
  | "contacts"
  | "endpoints"
  | "versions";

type DeviceForm = {
  additionalCategories: FormListItem[];
  additionalIdentifiers: FormListItem[];
  additionalNotes: FormListItem[];
  additionalTypes: FormListItem[];
  availabilityStatus: string;
  contacts: FormListItem[];
  endpoints: FormListItem[];
  location: string;
  manufacturer: string;
  modelNumber: string;
  name: string;
  serialNumber: string;
  status: string;
  url: string;
  versions: FormListItem[];
};

type RepeatableFieldsProps = {
  addLabel: string;
  children: (index: number) => ReactNode;
  emptyLabel: string;
  entries: FormListItem[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  title: string;
};

const initialForm: DeviceForm = {
  additionalCategories: [],
  additionalIdentifiers: [],
  additionalNotes: [],
  additionalTypes: [],
  availabilityStatus: "available",
  contacts: [],
  endpoints: [],
  location: "",
  manufacturer: "",
  modelNumber: "",
  name: "",
  serialNumber: "",
  status: "active",
  url: "",
  versions: [],
};

const emptyListItem: Record<RepeatableField, FormListItem> = {
  additionalCategories: { code: "", display: "", system: "" },
  additionalIdentifiers: { system: "", value: "" },
  additionalNotes: { text: "" },
  additionalTypes: { code: "", display: "", system: "" },
  contacts: { system: "", use: "", value: "" },
  endpoints: { display: "", reference: "" },
  versions: { installDate: "", value: "" },
};

const categoryFor = (kind: DeviceKind) => ({
  code: kind,
  display: kind === "software" ? "Software" : "Material",
});

const RepeatableFields = ({
  addLabel,
  children,
  emptyLabel,
  entries,
  onAdd,
  onRemove,
  title,
}: RepeatableFieldsProps) => (
  <section className="device-registration-repeatable">
    <div className="device-registration-repeatable-header">
      <h3>{title}</h3>
      <Button
        aria-label={addLabel}
        className="device-registration-icon-button"
        size="sm"
        type="button"
        variant="outline-primary"
        onClick={onAdd}
      >
        +
      </Button>
    </div>
    {entries.length === 0 ? (
      <p className="device-registration-empty">{emptyLabel}</p>
    ) : (
      entries.map((_, index) => (
        <div className="device-registration-repeatable-row" key={index}>
          {children(index)}
          <Button
            aria-label={i18n.t("button.remove")}
            size="sm"
            type="button"
            variant="outline-danger"
            onClick={() => onRemove(index)}
          >
            ×
          </Button>
        </div>
      ))
    )}
  </section>
);

const deviceFromForm = (form: DeviceForm, kind: DeviceKind): FhirDevice => {
  const device: FhirDevice = {
    resourceType: "Device",
    displayName: form.name,
    name: [
      {
        value: form.name,
        type: "user-friendly-name",
        display: true,
      },
    ],
    status: form.status,
  };

  if (form.availabilityStatus) {
    device.availabilityStatus = {
      coding: [{ code: form.availabilityStatus }],
      text: form.availabilityStatus,
    };
  }
  const additionalIdentifiers = form.additionalIdentifiers
    .filter((item) => item.value)
    .map((item) => ({
      value: item.value,
      ...(item.system ? { system: item.system } : {}),
    }));
  if (additionalIdentifiers.length > 0) {
    device.identifier = additionalIdentifiers;
  }
  if (form.location) {
    device.location = { display: form.location };
  }
  if (form.manufacturer) {
    device.manufacturer = form.manufacturer;
  }
  if (form.modelNumber) {
    device.modelNumber = form.modelNumber;
  }
  const additionalNotes = form.additionalNotes
    .filter((item) => item.text)
    .map((item) => ({ text: item.text }));
  if (additionalNotes.length > 0) {
    device.note = additionalNotes;
  }
  if (form.serialNumber) {
    device.serialNumber = form.serialNumber;
  }
  if (form.url) {
    device.url = form.url;
  }
  const additionalVersions = form.versions
    .filter((item) => item.value)
    .map((item) => ({
      value: item.value,
      ...(item.installDate ? { installDate: item.installDate } : {}),
    }));
  if (additionalVersions.length > 0) {
    device.version = additionalVersions;
  }
  const additionalTypes = form.additionalTypes
    .filter((item) => item.code || item.display)
    .map((item) => ({
      coding: [
        {
          ...(item.system ? { system: item.system } : {}),
          ...(item.code ? { code: item.code } : {}),
          ...(item.display ? { display: item.display } : {}),
        },
      ],
      ...(item.display ? { text: item.display } : {}),
    }));
  if (additionalTypes.length > 0) {
    device.type = additionalTypes;
  }
  const additionalCategories = form.additionalCategories
    .filter((item) => item.code || item.display)
    .map((item) => ({
      coding: [
        {
          ...(item.system ? { system: item.system } : {}),
          ...(item.code ? { code: item.code } : {}),
          ...(item.display ? { display: item.display } : {}),
        },
      ],
      ...(item.display ? { text: item.display } : {}),
    }));
  if (additionalCategories.length > 0) {
    device.category = additionalCategories;
  }
  const contacts = form.contacts
    .filter((item) => item.value)
    .map((item) => ({
      value: item.value,
      ...(item.system ? { system: item.system } : {}),
      ...(item.use ? { use: item.use } : {}),
    }));
  if (contacts.length > 0) {
    device.contact = contacts;
  }
  const endpoints = form.endpoints
    .filter((item) => item.reference || item.display)
    .map((item) => ({
      ...(item.reference ? { reference: item.reference } : {}),
      ...(item.display ? { display: item.display } : {}),
    }));
  if (endpoints.length > 0) {
    device.endpoint = endpoints;
  }

  return addRequiredCategory(device, kind);
};

const DeviceRegistration = ({ kind }: DeviceRegistrationProps) => {
  const navigate = useNavigate();
  const client = useMemo(
    () => new Client({ baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir" }),
    [],
  );
  const [form, setForm] = useState<DeviceForm>(initialForm);
  const [mode, setMode] = useState<RegistrationMode>("form");
  const [rawJson, setRawJson] = useState('{\n  "resourceType": "Device"\n}');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{
    text: string;
    variant: "danger" | "success";
  }>();

  const titleKey =
    kind === "software"
      ? "title.softwareregistration"
      : "title.materialregistration";
  const listRoute = kind === "software" ? "/SoftwareList" : "/MaterialList";
  const detailsRoute =
    kind === "software" ? "/SoftwareViewer/" : "/MaterialViewer/";
  const category = categoryFor(kind);

  const updateForm = (field: keyof DeviceForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateListItem = (
    field: RepeatableField,
    index: number,
    property: string,
    value: string,
  ) => {
    setForm((current) => {
      const items = [...current[field]];
      items[index] = { ...items[index], [property]: value };
      return { ...current, [field]: items };
    });
  };

  const addListItem = (field: RepeatableField) => {
    setForm((current) => ({
      ...current,
      [field]: [...current[field], { ...emptyListItem[field] }],
    }));
  };

  const removeListItem = (field: RepeatableField, index: number) => {
    setForm((current) => ({
      ...current,
      [field]: current[field].filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const updateRawJson = (value: string) => {
    setRawJson(value);
  };

  const buildDevice = (): FhirDevice | undefined => {
    if (mode === "form") {
      if (!form.name.trim()) {
        setMessage({
          text: i18n.t("devices.registration.nameRequired"),
          variant: "danger",
        });
        return undefined;
      }
      return deviceFromForm(form, kind);
    }

    try {
      const parsed: unknown = JSON.parse(rawJson);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setMessage({
          text: i18n.t("devices.registration.jsonObjectRequired"),
          variant: "danger",
        });
        return undefined;
      }

      const device = parsed as FhirDevice;
      if (device.resourceType && device.resourceType !== "Device") {
        setMessage({
          text: i18n.t("devices.registration.deviceResourceRequired"),
          variant: "danger",
        });
        return undefined;
      }
      return addRequiredCategory(device, kind);
    } catch {
      setMessage({
        text: i18n.t("devices.registration.invalidJson"),
        variant: "danger",
      });
      return undefined;
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage(undefined);

    const device = buildDevice();
    if (!device) {
      return;
    }

    setIsSaving(true);
    try {
      const created = await client.create({
        resourceType: "Device",
        body: device as any,
      });
      if (created.id) {
        navigate(`${detailsRoute}${created.id}`);
        return;
      }
      setMessage({
        text: i18n.t("devices.registration.success"),
        variant: "success",
      });
    } catch (error) {
      console.error("Unable to create Device", error);
      setMessage({
        text: i18n.t("devices.registration.creationFailed"),
        variant: "danger",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <VulcanPage titleKey={titleKey} loading={false} needsLogin>
      <div className="device-registration-page">
        <p className="device-registration-intro">
          {i18n.t("devices.registration.categoryNotice", {
            category: category.display,
          })}
        </p>

        <div
          className="device-registration-modes"
          role="group"
          aria-label={i18n.t("devices.registration.mode")}
        >
          <Button
            variant={mode === "form" ? "primary" : "outline-primary"}
            onClick={() => setMode("form")}
          >
            {i18n.t("button.structuredForm")}
          </Button>
          <Button
            variant={mode === "json" ? "primary" : "outline-primary"}
            onClick={() => setMode("json")}
          >
            {i18n.t("button.rawJson")}
          </Button>
          <Button
            variant={mode === "questionnaire" ? "primary" : "outline-primary"}
            onClick={() => setMode("questionnaire")}
          >
            {i18n.t("button.questionnaire")}
          </Button>
        </div>

        {mode === "questionnaire" ? (
          <DeviceQuestionnaireRegistration kind={kind} onBack={() => navigate(listRoute)} />
        ) : (
        <Form onSubmit={handleSubmit}>
          {mode === "form" ? (
            <>
              <Card className="device-registration-card">
                <Card.Header>
                  {i18n.t("devices.registration.formTitle")}
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={8}>
                      <Form.Group className="mb-3" controlId="device-name">
                        <Form.Label>{i18n.t("label.name")}</Form.Label>
                        <Form.Control
                          required
                          value={form.name}
                          onChange={(event) =>
                            updateForm("name", event.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="device-status">
                        <Form.Label>{i18n.t("label.status")}</Form.Label>
                        <Form.Select
                          value={form.status}
                          onChange={(event) =>
                            updateForm("status", event.target.value)
                          }
                        >
                          <option value="active">active</option>
                          <option value="inactive">inactive</option>
                          <option value="entered-in-error">
                            entered-in-error
                          </option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group
                        className="mb-3"
                        controlId="device-availability"
                      >
                        <Form.Label>{i18n.t("label.availability")}</Form.Label>
                        <Form.Select
                          value={form.availabilityStatus}
                          onChange={(event) =>
                            updateForm("availabilityStatus", event.target.value)
                          }
                        >
                          <option value="available">available</option>
                          <option value="lost">lost</option>
                          <option value="damaged">damaged</option>
                          <option value="destroyed">destroyed</option>
                          <option value="entered-in-error">
                            entered-in-error
                          </option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group
                        className="mb-3"
                        controlId="device-manufacturer"
                      >
                        <Form.Label>{i18n.t("label.manufacturer")}</Form.Label>
                        <Form.Control
                          value={form.manufacturer}
                          onChange={(event) =>
                            updateForm("manufacturer", event.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group
                        className="mb-3"
                        controlId="device-model-number"
                      >
                        <Form.Label>{i18n.t("label.modelNumber")}</Form.Label>
                        <Form.Control
                          value={form.modelNumber}
                          onChange={(event) =>
                            updateForm("modelNumber", event.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group
                        className="mb-3"
                        controlId="device-serial-number"
                      >
                        <Form.Label>{i18n.t("label.serialNumber")}</Form.Label>
                        <Form.Control
                          value={form.serialNumber}
                          onChange={(event) =>
                            updateForm("serialNumber", event.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="device-location">
                        <Form.Label>{i18n.t("label.location")}</Form.Label>
                        <Form.Control
                          value={form.location}
                          onChange={(event) =>
                            updateForm("location", event.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="device-url">
                        <Form.Label>{i18n.t("label.url")}</Form.Label>
                        <Form.Control
                          type="url"
                          value={form.url}
                          onChange={(event) =>
                            updateForm("url", event.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="device-registration-inline-lists">
                    <RepeatableFields
                      addLabel={i18n.t("button.add")}
                      emptyLabel={i18n.t("devices.registration.emptyList")}
                      entries={form.additionalIdentifiers}
                      onAdd={() => addListItem("additionalIdentifiers")}
                      onRemove={(index) =>
                        removeListItem("additionalIdentifiers", index)
                      }
                      title={i18n.t(
                        "devices.registration.additionalIdentifiers",
                      )}
                    >
                      {(index) => (
                        <Row className="flex-grow-1">
                          <Col md={6}>
                            <Form.Control
                              aria-label={i18n.t("label.identifier")}
                              placeholder={i18n.t("label.identifier")}
                              value={form.additionalIdentifiers[index].value}
                              onChange={(event) =>
                                updateListItem(
                                  "additionalIdentifiers",
                                  index,
                                  "value",
                                  event.target.value,
                                )
                              }
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              aria-label={i18n.t("label.identifierSystem")}
                              placeholder={i18n.t("label.identifierSystem")}
                              value={form.additionalIdentifiers[index].system}
                              onChange={(event) =>
                                updateListItem(
                                  "additionalIdentifiers",
                                  index,
                                  "system",
                                  event.target.value,
                                )
                              }
                            />
                          </Col>
                        </Row>
                      )}
                    </RepeatableFields>
                    <RepeatableFields
                      addLabel={i18n.t("button.add")}
                      emptyLabel={i18n.t("devices.registration.emptyList")}
                      entries={form.additionalTypes}
                      onAdd={() => addListItem("additionalTypes")}
                      onRemove={(index) =>
                        removeListItem("additionalTypes", index)
                      }
                      title={i18n.t("devices.registration.additionalTypes")}
                    >
                      {(index) => (
                        <Row className="flex-grow-1">
                          {(["code", "display", "system"] as const).map(
                            (field) => (
                              <Col md={4} key={field}>
                                <Form.Control
                                  aria-label={i18n.t(
                                    `label.type${field[0].toUpperCase()}${field.slice(1)}`,
                                  )}
                                  placeholder={i18n.t(
                                    `label.type${field[0].toUpperCase()}${field.slice(1)}`,
                                  )}
                                  value={form.additionalTypes[index][field]}
                                  onChange={(event) =>
                                    updateListItem(
                                      "additionalTypes",
                                      index,
                                      field,
                                      event.target.value,
                                    )
                                  }
                                />
                              </Col>
                            ),
                          )}
                        </Row>
                      )}
                    </RepeatableFields>
                    <RepeatableFields
                      addLabel={i18n.t("button.add")}
                      emptyLabel={i18n.t("devices.registration.emptyList")}
                      entries={form.versions}
                      onAdd={() => addListItem("versions")}
                      onRemove={(index) => removeListItem("versions", index)}
                      title={i18n.t("devices.registration.additionalVersions")}
                    >
                      {(index) => (
                        <Row className="flex-grow-1">
                          <Col md={6}>
                            <Form.Control
                              aria-label={i18n.t("label.version")}
                              placeholder={i18n.t("label.version")}
                              value={form.versions[index].value}
                              onChange={(event) =>
                                updateListItem(
                                  "versions",
                                  index,
                                  "value",
                                  event.target.value,
                                )
                              }
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              aria-label={i18n.t("label.installDate")}
                              placeholder={i18n.t("label.installDate")}
                              type="datetime-local"
                              value={form.versions[index].installDate}
                              onChange={(event) =>
                                updateListItem(
                                  "versions",
                                  index,
                                  "installDate",
                                  event.target.value,
                                )
                              }
                            />
                          </Col>
                        </Row>
                      )}
                    </RepeatableFields>
                    <RepeatableFields
                      addLabel={i18n.t("button.add")}
                      emptyLabel={i18n.t("devices.registration.emptyList")}
                      entries={form.additionalCategories}
                      onAdd={() => addListItem("additionalCategories")}
                      onRemove={(index) =>
                        removeListItem("additionalCategories", index)
                      }
                      title={i18n.t(
                        "devices.registration.additionalCategories",
                      )}
                    >
                      {(index) => (
                        <Row className="flex-grow-1">
                          {(["code", "display", "system"] as const).map(
                            (field) => (
                              <Col md={4} key={field}>
                                <Form.Control
                                  aria-label={i18n.t(
                                    `label.category${field[0].toUpperCase()}${field.slice(1)}`,
                                  )}
                                  placeholder={i18n.t(
                                    `label.category${field[0].toUpperCase()}${field.slice(1)}`,
                                  )}
                                  value={
                                    form.additionalCategories[index][field]
                                  }
                                  onChange={(event) =>
                                    updateListItem(
                                      "additionalCategories",
                                      index,
                                      field,
                                      event.target.value,
                                    )
                                  }
                                />
                              </Col>
                            ),
                          )}
                        </Row>
                      )}
                    </RepeatableFields>
                    <RepeatableFields
                      addLabel={i18n.t("button.add")}
                      emptyLabel={i18n.t("devices.registration.emptyList")}
                      entries={form.contacts}
                      onAdd={() => addListItem("contacts")}
                      onRemove={(index) => removeListItem("contacts", index)}
                      title={i18n.t("devices.registration.contacts")}
                    >
                      {(index) => (
                        <Row className="flex-grow-1">
                          {(["system", "value", "use"] as const).map(
                            (field) => (
                              <Col md={4} key={field}>
                                <Form.Control
                                  aria-label={i18n.t(
                                    `label.contact${field[0].toUpperCase()}${field.slice(1)}`,
                                  )}
                                  placeholder={i18n.t(
                                    `label.contact${field[0].toUpperCase()}${field.slice(1)}`,
                                  )}
                                  value={form.contacts[index][field]}
                                  onChange={(event) =>
                                    updateListItem(
                                      "contacts",
                                      index,
                                      field,
                                      event.target.value,
                                    )
                                  }
                                />
                              </Col>
                            ),
                          )}
                        </Row>
                      )}
                    </RepeatableFields>
                    <RepeatableFields
                      addLabel={i18n.t("button.add")}
                      emptyLabel={i18n.t("devices.registration.emptyList")}
                      entries={form.endpoints}
                      onAdd={() => addListItem("endpoints")}
                      onRemove={(index) => removeListItem("endpoints", index)}
                      title={i18n.t("devices.registration.endpoints")}
                    >
                      {(index) => (
                        <Row className="flex-grow-1">
                          <Col md={6}>
                            <Form.Control
                              aria-label={i18n.t("label.endpointReference")}
                              placeholder={i18n.t("label.endpointReference")}
                              value={form.endpoints[index].reference}
                              onChange={(event) =>
                                updateListItem(
                                  "endpoints",
                                  index,
                                  "reference",
                                  event.target.value,
                                )
                              }
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              aria-label={i18n.t("label.display")}
                              placeholder={i18n.t("label.display")}
                              value={form.endpoints[index].display}
                              onChange={(event) =>
                                updateListItem(
                                  "endpoints",
                                  index,
                                  "display",
                                  event.target.value,
                                )
                              }
                            />
                          </Col>
                        </Row>
                      )}
                    </RepeatableFields>
                    <RepeatableFields
                      addLabel={i18n.t("button.add")}
                      emptyLabel={i18n.t("devices.registration.emptyList")}
                      entries={form.additionalNotes}
                      onAdd={() => addListItem("additionalNotes")}
                      onRemove={(index) =>
                        removeListItem("additionalNotes", index)
                      }
                      title={i18n.t("devices.registration.additionalNotes")}
                    >
                      {(index) => (
                        <Form.Control
                          aria-label={i18n.t("label.note")}
                          placeholder={i18n.t("label.note")}
                          value={form.additionalNotes[index].text}
                          onChange={(event) =>
                            updateListItem(
                              "additionalNotes",
                              index,
                              "text",
                              event.target.value,
                            )
                          }
                        />
                      )}
                    </RepeatableFields>
                  </div>
                </Card.Body>
              </Card>
            </>
          ) : (
            <Card className="device-registration-card">
              <Card.Header>
                {i18n.t("devices.registration.jsonTitle")}
              </Card.Header>
              <Card.Body>
                <Form.Group controlId="device-json">
                  <Form.Label>
                    {i18n.t("devices.registration.jsonHelp")}
                  </Form.Label>
                  <div className="device-registration-json">
                    <Editor
                      defaultLanguage="json"
                      height="75vh"
                      options={{
                        automaticLayout: true,
                        minimap: { enabled: false },
                        quickSuggestions: false,
                        suggestOnTriggerCharacters: false,
                        tabSize: 2,
                      }}
                      theme="vs-light"
                      value={rawJson}
                      onChange={(value) => updateRawJson(value ?? "")}
                    />
                  </div>
                </Form.Group>
              </Card.Body>
            </Card>
          )}

          {message && <Alert variant={message.variant}>{message.text}</Alert>}

          <div className="device-registration-actions">
            <Button type="submit" disabled={isSaving}>
              {i18n.t("button.createDevice")}
            </Button>
            <Button
              variant="outline-secondary"
              type="button"
              onClick={() => navigate(listRoute)}
            >
              {i18n.t("button.back")}
            </Button>
          </div>
        </Form>
        )}
      </div>
    </VulcanPage>
  );
};

export default DeviceRegistration;
